import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import { raiseToast } from '../../../services/redux/reducers/toast/toast';
import { TextInputField, Button, DropDownField, InputSwitchField, AutoCompleteField } from "../../../helpers";
import { insertPettyCashAccount, updatePettyCashAccount } from '../../../services/api/pettyCashAccounts';
import { PettyCashAccountsSchem } from '../../../validation/addPettyCashAccountValidation';
import { getFullName, validateInputs } from '../../../utils';
import { updatePettyCashAccountsData } from "../../../services/redux/reducers/pettyCashAccounts/pettyCashAccounts";

import { MultiSelectField } from "../../../helpers";
import { sortArrayOfObjectsInAlphabeticalOrder } from "../../../utils";

import { Tooltip } from "primereact/tooltip";

const AddPettyCashAccountModal = (props) => {
    const {initialValues, usersList, title, setVisibleAddPettyCashAccount, setPettyCashAccountLoading } = props;
    const [accountName, setAccountName] = useState('')
    const [selectedPMBranchId, setSelectedPMBranchId] = useState(null)
    const [selectedUsers, setSelectedUsers] = useState([])
    const [removedUsers, setRemovedUsers] = useState([])
    const [initialAccountUsers,setInitialAccountUsers] = useState([])
    const [primaryUserId, setPrimaryUserId] = useState('')
    const [formErrors, setFormErrors] = useState([])
    const [selectedPettyCashAccountsId, setSelectedPettyCashAccountsId] = useState(null)
    const userDetails = useSelector(state => state.userDetails)
    const userId = userDetails.userId
    const pettyCashAccountsData = useSelector(state => state.pettyCashAccounts.pettyCashAccountsData)

    const [accountUserSuggestions, setAccountUserSuggestions] = useState([])
    const [primaryUserPMBranches, setPrimaryUserPMBranches] = useState([])

    const allPMBranches = userDetails.PMCompanyDetails.companyBranches

    const dispatch = useDispatch()

    const validAccountNameRegex = /^[a-zA-Z0-9_\s]+$/

    useEffect(() => {
        if (initialValues) {
            setSelectedPettyCashAccountsId(initialValues.pettyCashAccountsId)
            setPrimaryUserId(initialValues.userId)
            setSelectedPMBranchId(initialValues.pmBranchId)
            setAccountName(initialValues.pettyCashAccount)
            const initialAccountUsers = initialValues.accountUsers.map(eachAccountuUser => {
                const relatedUser = usersList.find(eachUser => eachUser.userId === eachAccountuUser?.userId)
                if (relatedUser) {
                    return {
                        ...relatedUser,
                        fullName: getFullName(relatedUser.firstName, relatedUser.lastName)
                    }
                }
            })
            setSelectedUsers(initialAccountUsers)
            getPrimaryUserRelatedPMBranches(initialAccountUsers, initialValues.userId)
            const initialAccountUserList = initialAccountUsers.map(eachInitialAccountUser => eachInitialAccountUser?.userId)
            setInitialAccountUsers(initialAccountUserList)
        }
    }, [])

    useEffect(() => {
        const removed = [];
        initialAccountUsers.forEach(user => {
            if (!selectedUsers.some(selectedUser => selectedUser.userId === user)) {
                removed.push(user)
            }
        })
        setRemovedUsers(removed)
    }, [selectedUsers])

    const getUsersNamesFromIds = (usersList, errorArray) => {
        const userNames = [];
        usersList.forEach(user => {
            if (errorArray.includes(user.userId)) {
                userNames.push(`${user.firstName} ${user.lastName}`);
            }
        });
        return userNames;
    }

    const handleAddAccount = async () => {
        let selectedUserDetails = selectedUsers?.map(user => ({
            userId: user.userId,
            statusId: 1
        }))
        let selectedUserIds = selectedUsers?.map(user => user.userId)
        const data = {
            pettyCashAccount: accountName,
            userId: primaryUserId,
            pettyCashUsers: selectedUserDetails,
            pettyCashUsersIds: selectedUserIds,
            pmBranchId: selectedPMBranchId,
            createdBy: userId,
            updatedBy: userId,
            createdAt: new Date(),
            updatedAt: new Date(),
            accountType: 'Joint'
        }
        const validationRes = await validateInputs(PettyCashAccountsSchem, data)
        if (validationRes.error) {
            setFormErrors(validationRes.error)
        }
        else {
            setFormErrors({})
            if(window.confirm("Are you sure to add account?")){
                setPettyCashAccountLoading(true)
                const insertResponse = await insertPettyCashAccount(data)
                setPettyCashAccountLoading(false)
                if (insertResponse.failure) {
                    const errorValue = insertResponse.data.error
                    const isLikelyJsonArray = typeof errorValue === 'string' && errorValue.startsWith('[') && errorValue.endsWith(']')
                
                    const errorDetail = isLikelyJsonArray
                        ? `The following pettyCashUsers users already exist: ${getUsersNamesFromIds(usersList, JSON.parse(errorValue)).join(',\n')}`
                        : errorValue
                    return dispatch(raiseToast({ severity: 'error', summary: "New Account", detail: errorDetail, life: 3000 }))
                }

                setVisibleAddPettyCashAccount(false)
                let newPettyCashAccount = insertResponse.data.newPettyCashAccount
                let newPettyCashUsers = insertResponse.data.newPettyCashUsers
                newPettyCashAccount.accountUsers = newPettyCashUsers.map(item => ({
                    userId: item.userId
                }))
                dispatch(updatePettyCashAccountsData([newPettyCashAccount, ...pettyCashAccountsData]))
                return dispatch(raiseToast({ severity: 'success', summary: "New Account", detail: 'Account has been created successfully.', life: 3000 }))
            }
        }
    }

    const handleEditAccount = async () =>{
        let selectedUserIds = selectedUsers?.map(user => user.userId)
        let updatedPrimaryUser = null
        if (initialValues?.userId != primaryUserId){
            updatedPrimaryUser = primaryUserId
        }
        let newPettyCashUserIds  = selectedUserIds.filter(id => !initialAccountUsers.includes(id));

        let newPettyCashUserDetails = newPettyCashUserIds?.map(userId => ({
            userId : userId,
            pettyCashAccountId : selectedPettyCashAccountsId,
            statusId : 1
        }));
        const data = {
            updatedPrimaryUser : updatedPrimaryUser,
            newPettyCashUsers: newPettyCashUserDetails,
            newPettyCashUserIds : newPettyCashUserIds,
            pmBranchId: selectedPMBranchId,
            removedUsers: removedUsers,
            updatedBy: userId,
            updatedAt: new Date(),
        }
        if(selectedUsers.length == 0){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'Please select atleast one account user.', life: 3000}))
        }
        if(!primaryUserId){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'Please select Primary Account Holder.', life: 3000}))
        }
        if(!selectedPMBranchId){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'Please select PM Branch.', life: 3000}))
        }
        setPettyCashAccountLoading(true)
        const updatedResponse = await updatePettyCashAccount(selectedPettyCashAccountsId, data)
        if (updatedResponse?.failure) {
            setPettyCashAccountLoading(false)
            const errorMessage = updatedResponse?.data?.error;
            try {
                const errorArray = JSON.parse(errorMessage);
                const userNamesWithError = getUsersNamesFromIds(usersList, errorArray);
                return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: `The following pettyCashUsers already exist: ` + userNamesWithError.join(',\n'), life: 3000}))
            } catch (error) {
                return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: errorMessage, life: 3000}))
            }
        }
        else{
            setPettyCashAccountLoading(false)
            setVisibleAddPettyCashAccount(false)
            const data = updatedResponse?.data

            let updatedPettyCashAccountsData = pettyCashAccountsData.map(account => {
                if (account?.pettyCashAccountsId === selectedPettyCashAccountsId) {
                    let updatedAccount = { ...account };

                    if (data?.data?.updatedPrimaryUser !== null) {
                        updatedAccount.userId = data.data.updatedPrimaryUser;
                    }
                    let updatedAccountUsers = [...updatedAccount.accountUsers];
                
                    if (data?.newPettyCashUserIds?.length > 0) {
                        data.newPettyCashUserIds.forEach(id => {
                            updatedAccountUsers.push({ userId: id });
                        });
                    }
                    updatedAccount.accountUsers = updatedAccountUsers;
                    updatedAccount.pmBranchId = selectedPMBranchId

                    if (data?.removedUsers?.length > 0) {
                        updatedAccount.accountUsers = updatedAccount.accountUsers.filter(user => !data.removedUsers.includes(user.userId));
                    }
                    return updatedAccount;
                } else {
                    return account;
                }
            });
            dispatch(updatePettyCashAccountsData(updatedPettyCashAccountsData))
            return dispatch(raiseToast({ severity: 'success', summary: "Edit Account", detail: 'Account has been updated successfully.', life: 3000 }))
        }
        
    }

    let updatedUserList = usersList.map(eachUser => ({
        ...eachUser, fullName: getFullName(eachUser.firstName, eachUser.lastName).trim()
    }))
    updatedUserList = sortArrayOfObjectsInAlphabeticalOrder(updatedUserList, "fullName")

    const handleUserChange = (event) => {
        setSelectedUsers(event.value)

        if (primaryUserId && !event.value.some(user => user.userId === primaryUserId)) {
            setPrimaryUserId(null)
            setSelectedPMBranchId(null)
            setPrimaryUserPMBranches([])
        }
        if(!event.value.length){
            setPrimaryUserPMBranches([])
        }
    }

    const getPrimaryUserRelatedPMBranches = (userList, primaryUserId) => {
        const relatedSelectedUser = userList.find(eachUser => eachUser.userId === primaryUserId)

        if (relatedSelectedUser) {
            const relatedUserRoles = relatedSelectedUser.userRoles
            if (relatedUserRoles.length) {
                const relatedPMBranchIds = relatedUserRoles.map(eachUserRole => eachUserRole.branchId)
                if (relatedPMBranchIds.includes(0)) {
                    setPrimaryUserPMBranches(allPMBranches)
                }
                else {
                    const relatedComapnyBanches = allPMBranches.filter(eachPMBranch => relatedPMBranchIds.includes(eachPMBranch.branchId))
                    setPrimaryUserPMBranches(relatedComapnyBanches)
                    if (relatedComapnyBanches.length === 1) {
                        setSelectedPMBranchId(relatedComapnyBanches[0].branchId)
                    }
                }
            }
            else {
                setPrimaryUserPMBranches([])
            }
        }
        else {
            setPrimaryUserPMBranches([])
        }
    }

    const handlePrimaryHolderChange = (e) => {
        setPrimaryUserId(e.target.value)
        setSelectedPMBranchId(null)
        getPrimaryUserRelatedPMBranches(selectedUsers, e.target.value)
    }

    const handlePmBranchChange = (e) => {
        setSelectedPMBranchId(e.target.value)
    }

    const searchAccountUsers = (event) => {
        const query = event.query.toLowerCase().trim()

        const _filteredAccountUsers = !query.length
            ? updatedUserList
            : updatedUserList.filter((eachUser) => {
                return eachUser.fullName.toLowerCase().includes(query)
            })

        setAccountUserSuggestions(_filteredAccountUsers)
    }

    const handleAccountNameChange = (e) => {
        setAccountName(e.target.value)
    }

    const handleAccountNameBlur = (e) => {
        const accountNameValue = e.target.value.trim()
        const isAccountNameValid = validAccountNameRegex.test(accountNameValue)
    
        if (accountNameValue !== "" && !isAccountNameValid) {
            return dispatch(raiseToast({ severity: 'error', summary: "New Account", detail: 'Only alphanumeric and underscores are allowed. No spaces or special characters.', life: 3000 }))
        }
    
        const formattedAccNameValue = accountNameValue.split(/\s+/)
            .map(eachWord => eachWord.charAt(0).toUpperCase() + eachWord.slice(1))
            .join('_')

        if (formattedAccNameValue !== accountName){
            setAccountName(formattedAccNameValue)
        }
    }

    return (
        <div className="grid mx-2 mt-1">
            <div className="flex flex-column col-12 mb-2 md:col-4 lg:col-4 lg:mb-0">
                <label style={{ fontSize: "12px", marginBottom: "3px" }}>
                    Account Name <span style={{ color: "red" }} className="from-required">*</span> <i className="pi pi-info-circle custom-tooltip-account-name" aria-hidden="true"></i>
                </label>
                <Tooltip target={`.custom-tooltip-account-name`} position="top" autoHide>
                    Account name can only contain letters, numbers, and underscores. Spaces are not allowed.
                </Tooltip>
                <TextInputField value={accountName} errorMessage={formErrors.pettyCashAccount} onChange={(e) => handleAccountNameChange(e)} onBlur={handleAccountNameBlur} disabled={title === 'EDIT'} required />
            </div>
            <div className="flex flex-column col-12 mb-2 md:col-4 lg:col-4 lg:mb-0">
                <label style={{ fontSize: "12px", marginBottom: "3px" }}>
                    Primary Account Holder <span style={{ color: "red" }} className="from-required">*</span> <i className="pi pi-info-circle custom-tooltip-primary-holder" aria-hidden="true"></i>
                </label>
                <Tooltip target={`.custom-tooltip-primary-holder`} position="top" autoHide>
                    Can’t find the user here? Add them as an account user below first!
                </Tooltip>
                <DropDownField optionLabel="fullName" optionValue="userId" value={primaryUserId} options={selectedUsers} errorMessage={formErrors.userId} onChange={(e) => handlePrimaryHolderChange(e)} required />
            </div>
            <div className="flex flex-column col-12 mb-2 md:col-4 lg:col-4 lg:mb-0">
                <DropDownField labelText="PM Branch" optionLabel="branchName" optionValue="branchId" value={selectedPMBranchId} options={primaryUserPMBranches} onChange={(e) => handlePmBranchChange(e)} errorMessage={formErrors.pmBranchId} required />
            </div>
            <div className="flex flex-column col-12 mb-2 md:col-12 lg:col-12 lg:mb-0">
                <AutoCompleteField labelText="Account Users" customFieldStyle={{ minheight: "34px" }} field="fullName" placeholder="Search user... " value={selectedUsers} searchMethod={searchAccountUsers} multiple onChange={(event) => handleUserChange(event)} dropdown errorMessage={formErrors.pettyCashUsersIds}
                    suggestions={accountUserSuggestions.filter(
                        eachSuggestionUser => !selectedUsers.some(eachSelectedUser => eachSelectedUser.userId === eachSuggestionUser.userId)
                    )}
                    required />
            </div>
            <div className='flex flex-row mt-2 justify-end mt-5 ml-auto'>
                <div className="ml-auto">
                    <Button label={title === 'ADD' ? 'ADD ACCOUNT' : 'SAVE CHANGES'} onClick={title === 'ADD' ? handleAddAccount : handleEditAccount} />
                </div>
            </div>
        </div>
    )
}

export default AddPettyCashAccountModal;
